'use client';

import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className", "elementType", "ownerState", "externalForwardedProps", "internalForwardedProps", "shouldForwardComponentProp"],
    _excluded2 = ["component", "slots", "slotProps"],
    _excluded3 = ["component"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import useForkRef from '@mui/utils/useForkRef';
import appendOwnerState from '@mui/utils/appendOwnerState';
import resolveComponentProps from '@mui/utils/resolveComponentProps';
import mergeSlotProps from '@mui/utils/mergeSlotProps';
/**
 * An internal function to create a Material UI slot.
 *
 * This is an advanced version of Base UI `useSlotProps` because Material UI allows leaf component to be customized via `component` prop
 * while Base UI does not need to support leaf component customization.
 *
 * @param {string} name: name of the slot
 * @param {object} parameters
 * @returns {[Slot, slotProps]} The slot's React component and the slot's props
 *
 * Note: the returned slot's props
 * - will never contain `component` prop.
 * - might contain `as` prop.
 */

export default function useSlot(
/**
 * The slot's name. All Material UI components should have `root` slot.
 *
 * If the name is `root`, the logic behaves differently from other slots,
 * e.g. the `externalForwardedProps` are spread to `root` slot but not other slots.
 */
name, parameters) {
  var className = parameters.className,
      initialElementType = parameters.elementType,
      ownerState = parameters.ownerState,
      externalForwardedProps = parameters.externalForwardedProps,
      internalForwardedProps = parameters.internalForwardedProps,
      _parameters$shouldFor = parameters.shouldForwardComponentProp,
      shouldForwardComponentProp = _parameters$shouldFor === void 0 ? false : _parameters$shouldFor,
      useSlotPropsParams = _objectWithoutProperties(parameters, _excluded);

  var rootComponent = externalForwardedProps.component,
      _externalForwardedPro = externalForwardedProps.slots,
      slots = _externalForwardedPro === void 0 ? _defineProperty({}, name, undefined) : _externalForwardedPro,
      _externalForwardedPro2 = externalForwardedProps.slotProps,
      slotProps = _externalForwardedPro2 === void 0 ? _defineProperty({}, name, undefined) : _externalForwardedPro2,
      other = _objectWithoutProperties(externalForwardedProps, _excluded2);

  var elementType = slots[name] || initialElementType; // `slotProps[name]` can be a callback that receives the component's ownerState.
  // `resolvedComponentsProps` is always a plain object.

  var resolvedComponentsProps = resolveComponentProps(slotProps[name], ownerState);

  var _mergeSlotProps = mergeSlotProps(_objectSpread(_objectSpread({
    className: className
  }, useSlotPropsParams), {}, {
    externalForwardedProps: name === 'root' ? other : undefined,
    externalSlotProps: resolvedComponentsProps
  })),
      _mergeSlotProps$props = _mergeSlotProps.props,
      slotComponent = _mergeSlotProps$props.component,
      mergedProps = _objectWithoutProperties(_mergeSlotProps$props, _excluded3),
      internalRef = _mergeSlotProps.internalRef;

  var ref = useForkRef(internalRef, resolvedComponentsProps === null || resolvedComponentsProps === void 0 ? void 0 : resolvedComponentsProps.ref, parameters.ref);
  var LeafComponent = name === 'root' ? slotComponent || rootComponent : slotComponent;
  var props = appendOwnerState(elementType, _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, name === 'root' && !rootComponent && !slots[name] && internalForwardedProps), name !== 'root' && !slots[name] && internalForwardedProps), mergedProps), LeafComponent && !shouldForwardComponentProp && {
    as: LeafComponent
  }), LeafComponent && shouldForwardComponentProp && {
    component: LeafComponent
  }), {}, {
    ref: ref
  }), ownerState);
  return [elementType, props];
}